import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { DBDef_RuntimeOrder, generalId } from '@app/advisor-shared';
import { _values, AssertionException, filterInstances, flatArray, MissingDataException, MUSTNeverHappenException, sortArray, timeout } from '@nu-art/ts-common';
import { ActionType_Order, DBDef_Action, DBDef_OrderGroup } from '@app/pathway-shared';
import { EpicAction_PostOrder, EpicAction_PostProtocol, ModuleFE_AdvisorWindowMessenger } from '../../../modules/ModuleFE_AdvisorWindowMessenger/index.js';
import { ModuleFE_BaseAdvisorRuntime } from '../../../modules/ModuleFE_BaseAdvisorRuntime.js';
import { RuntimeOrderStatus_Enum } from '@app/advisor-shared/_entity/runtime/runtime-order/consts';
import { ModuleFE_AdvisorSession, SessionKey_EHRInstallationId } from '../../advisor-session/index.js';
import { sendValidateCacheMessage } from '../../../consts.js';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_OrderGroup } from '../../../modules/static-modules/pathway.js';
import { ModuleFE_EMInterface } from '../em-interface/index.js';
import { ModuleFE_ActiveUser } from '../../advisor-session/ModuleFE_ActiveUser.js';
import { ModuleFE_RuntimeFinding } from '../runtime-finding/index.js';
import { ModuleFE_Advisor_OrderConfiguration, ModuleFE_Advisor_Resource } from '../../../modules/index.js';
export const dispatch_onRuntimeOrdersUpdated = new ThunderDispatcherV3('__onRuntimeOrdersUpdated');
export class ModuleFE_RuntimeOrder_Class extends ModuleFE_BaseAdvisorRuntime {
    _v1;
    __validateCache() {
        const encounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        const invalidOrders = this.cache.filter(order => order.encounterId !== encounterId);
        const moduleName = this.getName();
        if (!invalidOrders.length)
            return this.logDebug(`${moduleName} cache is valid`);
        this.logErrorBold(`${moduleName} has invalid data in cache`, invalidOrders);
        return sendValidateCacheMessage(invalidOrders, moduleName);
    }
    constructor() {
        super(DBDef_RuntimeOrder, dispatch_onRuntimeOrdersUpdated);
        this._v1 = {};
        this.setDefaultConfig({ postOrderActivationTTL: 30000 });
    }
    init() {
        super.init();
        this.setCacheFilter(ModuleFE_AdvisorSession.encounterFilter);
    }
    // ######################## API Callbacks ########################
    onRuntimeOrdersResponse = async (response) => {
        await this.onEntriesDeleted(response.deletedRuntimeOrders ?? []);
        await this.onEntriesUpdated(response.acceptedRuntimeOrders ?? [], false);
    };
    // ######################## Logic ########################
    assertRecommendedOrders = (rtOrderIds) => {
        for (const rtOrderId of rtOrderIds) {
            const rtOrder = this.cache.unique(rtOrderId);
            if (!rtOrder)
                throw new MissingDataException(`Could not find rtOrder for id ${rtOrderId}`);
            if (rtOrder.orderStatus !== RuntimeOrderStatus_Enum.RECOMMENDED)
                throw new AssertionException(`Cannot order rtOrder with id ${rtOrderId}, status is not "recommended"`);
        }
    };
    sendOrdersToEpic = async (rtOrderIds) => {
        const orders = rtOrderIds.map(id => {
            const rtOrder = this.cache.unique(id);
            if (!rtOrder)
                throw new MissingDataException(`Could not find rtOrder for id ${id}`);
            const order = ModuleFE_Advisor_Action.cache.unique(rtOrder.orderId);
            if (!order)
                throw new MissingDataException(`Could not find order for id ${rtOrder.orderId} under rtOrder with id ${id}`);
            return order;
        });
        return this.postEpicOrders(orders);
    };
    async postEpicOrders(orders) {
        for (const order of orders) {
            this.postEpicOrder(order);
            await timeout(500);
        }
    }
    postEpicMessage = (message, label) => {
        ModuleFE_ActiveUser.onOrderPlaced();
        this.logVerbose(`Posting ${label}: `, message);
        ModuleFE_AdvisorWindowMessenger.sendMessage(message);
    };
    postEpicOrder = (_order) => {
        const OrderSpecificQuestions = [];
        const order = typeof _order === 'string' ? ModuleFE_Advisor_Action.cache.unique(_order) : _order;
        if (!order)
            throw new MUSTNeverHappenException(`Could not resolve order for id ${_order}`);
        if (this.config.ordersToAddChiefComplaint?.includes(order._id)) {
            const chiefComplaint = ModuleFE_RuntimeFinding.cache.filter(finding => finding.isChiefComplaint);
            const reason = chiefComplaint.map(f => f._variableName).join(', ');
            OrderSpecificQuestions.push({ key: '105101513', Answers: { Answer: reason } });
        }
        const ehrInstallationId = SessionKey_EHRInstallationId.get();
        const configuration = ModuleFE_Advisor_OrderConfiguration.cache.find(config => config.ehrInstallationId === ehrInstallationId && config.orderId === order._id);
        let idToSend = ModuleFE_AdvisorSession.getUseEhrOrderId() ? (configuration?.ehrInstallationOrderId ?? order.name) : order._id;
        //Append suffix
        const resource = ModuleFE_Advisor_Resource.cache.unique(order.resourceId);
        const advisorConfig = ModuleFE_AdvisorSession.getAdvisorConfig();
        switch (resource?.type) {
            case 'Laboratory':
                if (advisorConfig?.aglFixing?.labs?.enabled)
                    idToSend += advisorConfig.aglFixing.labs.suffix ?? '';
                break;
            case 'Imaging':
                if (advisorConfig?.aglFixing?.radiology?.enabled)
                    idToSend += advisorConfig.aglFixing.radiology.suffix ?? '';
                break;
        }
        if (order.type === ActionType_Order && order.isOrderSet) {
            const protocolMessage = {
                action: EpicAction_PostProtocol,
                args: { ProtocolKey: idToSend },
            };
            this.postEpicMessage(protocolMessage, 'protocol (order set)');
            return;
        }
        const orderMessage = {
            args: {
                OrderKey: idToSend,
                OrderSpecificQuestions
            },
            action: EpicAction_PostOrder
        };
        if (!orderMessage.args.OrderSpecificQuestions?.length)
            delete orderMessage.args.OrderSpecificQuestions;
        this.postEpicMessage(orderMessage, 'order');
    };
    orderAllImpl = async (rtOrderIds) => {
        if (!rtOrderIds.length)
            return 0;
        try {
            //Assert all the ids are of runtime orders that are recommended
            this.assertRecommendedOrders(rtOrderIds);
            //Accept the orders
            await this.acceptOrders(rtOrderIds);
            // send all requests to epic
            const enableAgl = ModuleFE_AdvisorSession.getEnableAgl();
            if (enableAgl)
                await this.sendOrdersToEpic(rtOrderIds);
            return rtOrderIds.length;
        }
        catch (err) {
            this.logErrorBold(err);
            return 0;
        }
    };
    groupIsResolvedByOrders = (groupId, rtOrderIds) => {
        return rtOrderIds.some(rtOrderId => {
            const rtOrder = ModuleFE_RuntimeOrder.cache.unique(rtOrderId);
            if (!rtOrder)
                throw new MissingDataException(`Could not find rtOrder for id ${rtOrderId}`);
            const associatedGroupIds = flatArray(_values(rtOrder.protocolMapping ?? {}));
            return associatedGroupIds.includes(groupId);
        });
    };
    // ######################## Public Logic ########################
    createOrder = async (dbOrder, protocolId) => {
        const response = await ModuleFE_EMInterface.vv1.orders.createRunTimeOrders({
            orderIds: [dbOrder._id],
            protocolIds: protocolId === generalId ? [] : [protocolId]
        }).executeSync();
        // update cache
        await this.onRuntimeOrdersResponse(response);
        return response;
    };
    acceptOrders = async (rtOrderIds) => {
        const response = await ModuleFE_EMInterface.vv1.orders.acceptRuntimeOrders({ runtimeOrderIds: rtOrderIds }).executeSync();
        // update cache
        await this.onRuntimeOrdersResponse(response);
        return response;
    };
    rejectRuntimeOrders = async (rtOrderIds) => {
        const response = await ModuleFE_EMInterface.vv1.orders.rejectRuntimeOrders({
            runtimeOrderIds: rtOrderIds
        }).executeSync();
        await this.onEntriesUpdated(response.updatedRuntimeOrders ?? [], false);
        return response;
    };
    resolveOrderAllRtOrders = (rtOrderIds, groupIds) => {
        const resolvedIds = [...(rtOrderIds ?? [])];
        groupIds ??= [];
        if (!resolvedIds.length && !groupIds.length)
            return [];
        const allRecommendedOrders = ModuleFE_RuntimeOrder.cache.filter(rtOrder => rtOrder.orderStatus === RuntimeOrderStatus_Enum.RECOMMENDED && !rtOrder.extraMetadata?.rejected);
        groupIds.forEach(groupId => {
            if (this.groupIsResolvedByOrders(groupId, resolvedIds))
                return;
            const group = ModuleFE_Advisor_OrderGroup.cache.unique(groupId);
            if (!group)
                throw new MissingDataException(`Could not find order group for id ${groupId}`);
            const rtOrder = allRecommendedOrders.find(rtOrder => rtOrder.orderId === group.items[0].id);
            if (!rtOrder) {
                this.logError(`Missing rtOrder for order id ${group.items[0].id} under group with id ${groupId}`);
                return;
            }
            resolvedIds.push(rtOrder._id);
        });
        return filterInstances(resolvedIds.map(id => ModuleFE_RuntimeOrder.cache.unique(id)));
    };
    orderAll = async (rtOrderIds, groupIds) => {
        const placed = this.resolveOrderAllRtOrders(rtOrderIds, groupIds);
        if (!placed.length)
            return 0;
        return this.orderAllImpl(placed.map(rtOrder => rtOrder._id));
    };
    getResolvedPreConditionsForPathwayState = (state) => {
        if (!state.preconditions.length)
            return [];
        return filterInstances(state.preconditions.map(precondition => {
            const dbOrderIds = [];
            switch (precondition.itemPointer.dbKey) {
                case DBDef_Action.dbKey: {
                    const action = ModuleFE_Advisor_Action.cache.unique(precondition.itemPointer.id);
                    if (action?.type === 'order')
                        dbOrderIds.push(action._id);
                    break;
                }
                case DBDef_OrderGroup.dbKey: {
                    const group = ModuleFE_Advisor_OrderGroup.cache.unique(precondition.itemPointer.id);
                    if (group)
                        dbOrderIds.push(...group.items.map(i => i.id));
                }
            }
            const statuses = [RuntimeOrderStatus_Enum.RESULTED, RuntimeOrderStatus_Enum.COMPLETED];
            const rtOrders = this.cache.filter(rtOrder => statuses.includes(rtOrder.orderStatus) && dbOrderIds.includes(rtOrder.orderId));
            sortArray(rtOrders, i => i.timeAccepted, true);
            return rtOrders[0];
        }));
    };
}
export const ModuleFE_RuntimeOrder = new ModuleFE_RuntimeOrder_Class();
//# sourceMappingURL=ModuleFE_RuntimeOrder.js.map